<!--
  Client Portal v2: Allowance (plan-3-portal-completion.md section 3.5).

  Backend GET /api/portal/allowance is live (portal.py:1834). This page
  surfaces per-shopper allowance usage so clients can see how their
  per-person spending allocations are tracking for the current period.

  One table row per (shopper, store) grant in the current period:
    Shopper | Store | Granted | Consumed | Remaining | Utilization bar

  Empty state when no allowance grants exist (a client whose stores run on
  budget or card-only). Distributor-configured; Client Admins can VIEW only
  (no edit on this page -- allowance configuration lives on the distributor
  side per the routing model).
-->
<template>
  <v-container fluid class="portal-allowance" data-testid="portal-page-allowance">
    <h1 class="text-h5 mb-1">Allowance</h1>
    <p class="text-body-2 text-medium-emphasis mb-4">
      Per-shopper spending limits for the current period. Configured by your
      distributor -- contact your rep to adjust allowances.
    </p>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      data-testid="allowance-loading"
    />

    <v-alert
      v-else-if="errorMsg"
      type="error"
      variant="tonal"
      data-testid="allowance-error"
    >
      {{ errorMsg }}
    </v-alert>

    <v-card
      v-else-if="!allowances.length"
      class="pa-6 text-center"
      data-testid="allowance-empty"
    >
      <div class="text-subtitle-1 mb-1">No allowances configured</div>
      <div class="text-body-2 text-medium-emphasis">
        Your distributor has not set per-shopper allowances for any of your
        stores yet. Stores running on budget or card payment do not use
        individual allowances.
      </div>
    </v-card>

    <v-card v-else>
      <v-table data-testid="allowance-table">
        <thead>
          <tr>
            <th>Shopper</th>
            <th>Store</th>
            <th>Granted</th>
            <th>Consumed</th>
            <th>Remaining</th>
            <th style="width: 25%">Utilization</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in allowances"
            :key="row.grant_id"
            :data-testid="`allowance-row-${row.grant_id}`"
          >
            <td>{{ row.shopper_email || "--" }}</td>
            <td>{{ row.store_name }}</td>
            <td :data-testid="`allowance-granted-${row.grant_id}`">
              {{ formatDollars(row.granted_cents) }}
            </td>
            <td :data-testid="`allowance-consumed-${row.grant_id}`">
              {{ formatDollars(row.consumed_cents) }}
            </td>
            <td :data-testid="`allowance-remaining-${row.grant_id}`">
              {{ formatDollars(row.remaining_cents) }}
            </td>
            <td>
              <v-progress-linear
                :model-value="pctUsed(row)"
                :color="barColor(row)"
                height="10"
                :data-testid="`allowance-progress-${row.grant_id}`"
              />
              <span class="text-caption text-medium-emphasis">
                {{ pctUsed(row).toFixed(0) }}% used
              </span>
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card>
  </v-container>
</template>

<script>
import axios from "@/axios";

export default {
  name: "PortalAllowance",
  data() {
    return {
      allowances: [],
      loading: true,
      errorMsg: "",
    };
  },
  async mounted() {
    await this.fetchAllowance();
  },
  methods: {
    async fetchAllowance() {
      this.loading = true;
      this.errorMsg = "";
      try {
        const resp = await axios.get("/api/portal/allowance");
        this.allowances = resp?.data?.allowances ?? [];
      } catch (e) {
        this.errorMsg = "Could not load allowance data.";
      } finally {
        this.loading = false;
      }
    },
    pctUsed(row) {
      if (!row.granted_cents) return 0;
      return Math.min(100, (row.consumed_cents / row.granted_cents) * 100);
    },
    barColor(row) {
      const pct = this.pctUsed(row);
      if (pct >= 100) return "error";
      if (pct >= 85) return "warning";
      return "primary";
    },
    formatDollars(cents) {
      return `$${((cents || 0) / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
  },
};
</script>

<style scoped>
.portal-allowance {
  max-width: 960px;
}
</style>
